/**
 * useScanLabels — customer-facing terminology hook (Iter-32 Phase 3b).
 *
 * Fetches `/api/scan-labels` ONCE at app boot and caches the result in
 * both memory + localStorage. On network failure, returns a hardcoded
 * fallback that matches the current backend defaults so the dashboard
 * NEVER breaks because of the label endpoint.
 *
 * Contract:
 *   • `tiers(code)`, `urls(code)`, `states(code)`, `incidents(code)`
 *     — safe accessors. Unknown code → returns the code itself (never
 *     undefined, never throws).
 *   • The upcoming terminology pass edits the backend's
 *     `services/scan_labels.py` and every consumer of this hook
 *     picks up the new strings on next page load.
 *
 * DO NOT hardcode "Homepage" / "Donation page" / any customer-facing
 * label in JSX. Always call the hook.
 */
import { useEffect, useState } from "react";
import { API } from "../lib/api";

// Fallback catalog — kept in sync with `services/scan_labels.py`
// defaults. Used if the endpoint fetch fails or returns garbage. The
// dashboard SHOULD look identical on failure — that's the point.
export const SCAN_LABEL_FALLBACK = {
  schema_version: 1,
  tiers: {
    critical: "Mission Watch Protection",
    daily: "Mission Watch Audit",
    weekly: "Mission Watch Health Report",
  },
  urls: {
    homepage: "Homepage",
    donation: "Donation page",
    contact: "Contact page",
  },
  states: {
    passing: "Protected",
    verification_pending: "Verifying",
    monitoring_blocked: "Access Limited",
    confirmed_failure: "Needs Attention",
    not_checked: "Not Yet Verified",
  },
  incidents: {
    host_unreachable: "Website Reachability",
  },
};

const LS_KEY = "mw_scan_labels_v1";
const LS_TTL_MS = 60 * 60 * 1000; // 1h client cache (matches Cache-Control)

// In-memory promise so multiple components mounting at the same time
// share ONE network request.
let _inflight = null;

function _loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.fetched_at || !parsed.data) return null;
    if (Date.now() - parsed.fetched_at > LS_TTL_MS) return null;
    // Sanity: only accept a shape that still looks like the catalog.
    const d = parsed.data;
    if (!d.tiers || !d.states || !d.urls) return null;
    return d;
  } catch {
    return null;
  }
}

function _saveToStorage(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ fetched_at: Date.now(), data }));
  } catch {
    /* localStorage full / disabled — non-fatal */
  }
}

async function _fetchLabels() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${API}/scan-labels`, { credentials: "omit" });
      if (!res.ok) throw new Error(`scan-labels HTTP ${res.status}`);
      const data = await res.json();
      // Reject if the shape isn't recognizable — better to fall back
      // than to render `undefined` labels.
      if (!data || !data.tiers || !data.urls || !data.states) {
        throw new Error("scan-labels response missing required keys");
      }
      _saveToStorage(data);
      return data;
    } catch (e) {
      // Never rethrow — fallback is always safe.
      console.warn("useScanLabels: falling back to defaults —", e.message);
      return SCAN_LABEL_FALLBACK;
    } finally {
      // Allow refetch after 1s in case of transient failure — but the
      // component already got its answer via the resolved promise.
      setTimeout(() => { _inflight = null; }, 1000);
    }
  })();
  return _inflight;
}

/**
 * Returns { labels, ready, tiers, urls, states, incidents } where the
 * four helpers are `(code) => string`. Safe from first render.
 */
export function useScanLabels() {
  const cached = _loadFromStorage();
  const [labels, setLabels] = useState(cached || SCAN_LABEL_FALLBACK);
  const [ready, setReady] = useState(Boolean(cached));

  useEffect(() => {
    let mounted = true;
    // Skip network fetch if we already have a fresh cached copy.
    if (cached) return;
    _fetchLabels().then((data) => {
      if (mounted) {
        setLabels(data);
        setReady(true);
      }
    });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _get = (group, code) => {
    if (!code) return "";
    const g = labels?.[group];
    if (g && typeof g[code] === "string") return g[code];
    // Fall back to the same group in the shipped fallback.
    const f = SCAN_LABEL_FALLBACK[group];
    if (f && typeof f[code] === "string") return f[code];
    // Last resort: echo the code back so nothing goes blank.
    return code;
  };

  return {
    labels,
    ready,
    schema_version: labels?.schema_version || SCAN_LABEL_FALLBACK.schema_version,
    tiers:     (code) => _get("tiers", code),
    urls:      (code) => _get("urls", code),
    states:    (code) => _get("states", code),
    incidents: (code) => _get("incidents", code),
  };
}
